import { IconAlertTriangle } from '@tabler/icons-react';
import { FC, ReactNode, useEffect, useId, useState } from 'react';
import {
  findFirstMissingSectionField,
  ProfileCompletenessData,
  ProfileSection,
  RequiredFieldInfo,
} from '../utilities/profileCompleteness';

interface Props {
  icon: ReactNode;
  title: string;
  description: string;
  handleNavigate: () => void;
  data: ProfileCompletenessData;
  section: ProfileSection;
}

const AccountManagementCard: FC<Props> = ({
  icon,
  title,
  description,
  handleNavigate,
  data,
  section,
}) => {
  const warningId = useId();
  const [requiredField, setRequiredField] = useState<RequiredFieldInfo | null>(
    null,
  );

  useEffect(() => {
    setRequiredField(findFirstMissingSectionField(data, section));
  }, [data, section]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate();
    }
  };

  return (
    <div
      className="flex flex-col justify-between h-[205px] mt-6 border border-neutral-100 rounded-2xl w-full md:w-[364px] cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 focus-visible:ring-offset-2"
      onClick={() => handleNavigate()}
      onKeyDown={handleKeyDown}
      role="link"
      tabIndex={0}
      aria-label={
        requiredField
          ? `${title}. ${requiredField.message}`
          : `${title}. ${description}`
      }
      aria-describedby={requiredField ? warningId : undefined}
    >
      <div>
        <div className="px-6 pt-6" aria-hidden="true">
          {icon}
        </div>
        <div className="px-6 mt-4 md:mt-4 flex flex-col">
          <span className="text-base text-neutral-800 font-semibold">
            {title}
          </span>
          <span className="mt-2 text-sm text-neutral-500 font-normal">
            {description}
          </span>
        </div>
      </div>
      {requiredField && (
        <div
          id={warningId}
          className="flex items-center md:mt-0 pl-4 bg-status-warning-surface h-[52px] rounded-b-2xl"
        >
          <IconAlertTriangle
            className="text-status-warning-icon"
            size={24}
            aria-hidden="true"
          />
          <span className="ml-3.5 text-sm text-neutral-800">
            {requiredField.message}
          </span>
        </div>
      )}
    </div>
  );
};

export default AccountManagementCard;
